import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { createEffect, createEvent, createStore, sample } from "effector";
import { ILessonsContract, ILessonWithStatus } from "../model";
import { ICurrentLessonContract } from "./current";
import { IEnrollmentsContract } from "@features/enrollments/model";
import { IRouterContract } from "@features/shared/router";
import { ICurrentCourseContract } from "@features/courses/ui/current";
import { ITasksContract } from "@features/tasks/model";
import { reset } from "patronum";
import { routePathNameChanged } from "@features/app/model";
import { ICurrentCourse } from "@features/shared/entities/enrollments";
import { LessonStarted } from "@features/shared/events";

const lessonClicked = createEvent<ILessonWithStatus>();
const lessonModalClosed = createEvent();
const restartButtonClicked = createEvent<string | null>();
const viewButtonClicked = createEvent<string | null>();
const startButtonClicked = createEvent<string | null>();
const lastLessonStartedButtonClicked = createEvent<string | null>();
const lastLessonInTopicStartedButtonClicked = createEvent<string | null>(); // for topic page

type StartLessonFlowParams = {
    courseId: number;
    topicId: number;
    lessonId: number;
    source?: string | null;
    courseData?: ICurrentCourse;
    analytics?: {
        courseType: "challenge" | "course";
        lessonTitle: string;
        dayNumber?: number;
    };
};

const startLessonFlowRequested = createEvent<StartLessonFlowParams>();

const manualLessonStartedFx = createEffect<
    {
        courseType: "challenge" | "course";
        courseId: number;
        topicId: number;
        lessonId: number;
        lessonTitle: string;
        dayNumber?: number;
    },
    void
>((params) => {
    LessonStarted(params);
});

const $lessonModal = createStore<ILessonWithStatus | null>(null)
    .on(lessonClicked, (_, v) => v)
    .reset(lessonModalClosed);

const contract = new ModelContract(
    { $lessonModal },
    {
        lessonClicked,
        lessonModalClosed,
        restartButtonClicked,
        viewButtonClicked,
        startButtonClicked,
        lastLessonStartedButtonClicked,
        lastLessonInTopicStartedButtonClicked,
        startLessonFlowRequested
    },
    {},
    {}
);

export type IUiLessonContract = typeof contract;

export class LessonUiModule extends ModuleDescription<IUiLessonContract> {
    constructor(
        public lessonsModule: ILessonsContract,
        public currentLessonModule: ICurrentLessonContract,
        public currentCourseModule: ICurrentCourseContract,
        public enrollmentsModule: IEnrollmentsContract,
        public tasksModule: ITasksContract,
        public routerModule: IRouterContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            clock: [startButtonClicked, lastLessonStartedButtonClicked, lastLessonInTopicStartedButtonClicked],
            source: {
                enrollmentsCourses: this.enrollmentsModule.stores.$enrollmentsCourses,
                currentCourseId: this.currentCourseModule.stores.$currentCourseId
            },
            filter: (s) =>
                s.currentCourseId !== null && !s.enrollmentsCourses.find((c) => c.course_id === s.currentCourseId),
            fn: (s) => s.currentCourseId!,
            target: this.enrollmentsModule.events.enrollCourse
        });

        sample({
            clock: startButtonClicked,
            source: {
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                currentCourseId: this.currentCourseModule.stores.$currentCourseId,
                lessonModal: $lessonModal
            },
            filter: (s) =>
                s.currentCourseId !== null &&
                s.lessonModal !== null && !s.enrollmentsTopics.find((t) => t.topic_id === s.lessonModal!.topic_id),
            fn: (s) => ({ topicId: s.lessonModal!.topic_id, courseId: s.currentCourseId! }),
            target: this.enrollmentsModule.events.enrollTopic
        });

        sample({
            clock: startButtonClicked,
            source: {
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                lessonModal: $lessonModal
            },
            filter: (s) =>
                s.lessonModal !== null && !s.enrollmentsLessons.find((l) => l.lesson_id === s.lessonModal!.id),

            fn: (s) => ({ lessonId: s.lessonModal!.id, topicId: s.lessonModal!.topic_id }),
            target: this.enrollmentsModule.events.enrollLesson
        });

        sample({
            clock: restartButtonClicked,
            source: { enrollmentTasks: this.enrollmentsModule.stores.$enrollmentsTasks, lessonModal: $lessonModal },
            filter: (s) => s.lessonModal !== null && s.enrollmentTasks.length > 0,
            fn: (s) => {
                const taskIds = s.enrollmentTasks
                    .filter((enrollmentTask) => enrollmentTask.lesson_id === s.lessonModal!.id)
                    .map((et) => et.task_id);

                return { lessonId: s.lessonModal!.id, tasksIds: taskIds };
            },
            target: this.tasksModule.events.tasksReset
        });

        sample({
            clock: lastLessonStartedButtonClicked,
            source: {
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                currentCourseId: this.currentCourseModule.stores.$currentCourseId,
                lastLesson: this.lessonsModule.stores.$lastLesson
            },
            filter: (s) =>
                s.currentCourseId !== null &&
                s.lastLesson !== null && !s.enrollmentsTopics.find((t) => t.topic_id === s.lastLesson!.topic_id),
            fn: (s) => ({ topicId: s.lastLesson!.topic_id, courseId: s.currentCourseId! }),
            target: this.enrollmentsModule.events.enrollTopic
        });

        sample({
            clock: lastLessonStartedButtonClicked,
            source: {
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                lastLesson: this.lessonsModule.stores.$lastLesson
            },
            filter: (s) => s.lastLesson !== null && !s.enrollmentsLessons.find((l) => l.lesson_id === s.lastLesson!.id),

            fn: (s) => ({ lessonId: s.lastLesson!.id, topicId: s.lastLesson!.topic_id }),
            target: this.enrollmentsModule.events.enrollLesson
        });

        sample({
            clock: lastLessonInTopicStartedButtonClicked,
            source: {
                enrollmentsTopics: this.enrollmentsModule.stores.$enrollmentsTopics,
                currentCourseId: this.currentCourseModule.stores.$currentCourseId,
                lastLesson: this.lessonsModule.stores.$lastLessonInTopic
            },
            filter: (s) =>
                s.currentCourseId !== null &&
                s.lastLesson !== null && !s.enrollmentsTopics.find((t) => t.topic_id === s.lastLesson!.topic_id),
            fn: (s) => ({ topicId: s.lastLesson!.topic_id, courseId: s.currentCourseId! }),
            target: this.enrollmentsModule.events.enrollTopic
        });

        sample({
            clock: lastLessonInTopicStartedButtonClicked,
            source: {
                enrollmentsLessons: this.enrollmentsModule.stores.$enrollmentsLessons,
                lastLessonInTopic: this.lessonsModule.stores.$lastLessonInTopic
            },
            filter: (s) =>
                s.lastLessonInTopic !== null &&
                !s.enrollmentsLessons.find((l) => l.lesson_id === s.lastLessonInTopic!.id),

            fn: (s) => ({ lessonId: s.lastLessonInTopic!.id, topicId: s.lastLessonInTopic!.topic_id }),
            target: this.enrollmentsModule.events.enrollLesson
        });

        sample({
            clock: startLessonFlowRequested,
            source: this.enrollmentsModule.stores.$enrollmentsCourses,
            filter: (courses, params) => !courses.some((course) => course.course_id === params.courseId),
            fn: (_, params) => params.courseId,
            target: this.enrollmentsModule.events.enrollCourse
        });

        sample({
            clock: startLessonFlowRequested,
            source: this.enrollmentsModule.stores.$enrollmentsTopics,
            filter: (topics, params) => !topics.some((topic) => topic.topic_id === params.topicId),
            fn: (_, params) => ({ courseId: params.courseId, topicId: params.topicId }),
            target: this.enrollmentsModule.events.enrollTopic
        });

        sample({
            clock: startLessonFlowRequested,
            source: this.enrollmentsModule.stores.$enrollmentsLessons,
            filter: (lessons, params) => !lessons.some((lesson) => lesson.lesson_id === params.lessonId),
            fn: (_, params) => ({
                topicId: params.topicId,
                lessonId: params.lessonId,
                skipAnalytics: Boolean(params.analytics)
            }),
            target: this.enrollmentsModule.events.enrollLesson
        });

        sample({
            clock: startLessonFlowRequested,
            filter: (params) => Boolean(params.courseData),
            fn: (params) => params.courseData!,
            target: this.currentCourseModule.events.currentCourseChanged
        });

        sample({
            clock: startLessonFlowRequested,
            filter: (params) => Boolean(params.courseData?.id),
            fn: (params) => params.courseData!.id,
            target: this.currentCourseModule.events.currentCourseIdChanged
        });

        sample({
            clock: startLessonFlowRequested,
            filter: (params) => Boolean(params.analytics),
            fn: (params) => ({
                courseType: params.analytics!.courseType,
                courseId: params.courseId,
                topicId: params.topicId,
                lessonId: params.lessonId,
                lessonTitle: params.analytics!.lessonTitle,
                dayNumber: params.analytics!.dayNumber
            }),
            target: manualLessonStartedFx
        });

        sample({
            clock: [restartButtonClicked, startButtonClicked],
            source: {
                currentCourseId: this.currentCourseModule.stores.$currentCourseId,
                lessonModal: $lessonModal
            },
            filter: (s) => s.currentCourseId !== null,
            fn: (s, p) => {
                if (!p)
                    return `/course/${s.currentCourseId}/topic/${s.lessonModal?.topic_id}/lesson/${s.lessonModal?.id}`;
                return `/course/${s.currentCourseId}/topic/${s.lessonModal?.topic_id}/lesson/${s.lessonModal?.id}?source=${p}`;
            },
            target: this.routerModule.events.routerPush
        });

        sample({
            clock: viewButtonClicked,
            source: {
                currentCourseId: this.currentCourseModule.stores.$currentCourseId,
                lessonModal: $lessonModal
            },
            filter: (s) => s.currentCourseId !== null,
            fn: (s, p) => {
                if (!p)
                    return `/course/${s.currentCourseId}/topic/${s.lessonModal?.topic_id}/lesson/${s.lessonModal?.id}?mode=view`;
                return `/course/${s.currentCourseId}/topic/${s.lessonModal?.topic_id}/lesson/${s.lessonModal?.id}?mode=view&source=${p}`;
            },
            target: this.routerModule.events.routerPush
        });

        sample({
            clock: lastLessonStartedButtonClicked,
            source: {
                currentCourseId: this.currentCourseModule.stores.$currentCourseId,
                lastLesson: this.lessonsModule.stores.$lastLesson
            },
            filter: (s) => s.currentCourseId !== null && s.lastLesson !== null,
            fn: (s, p) => {
                if (!p)
                    return `/course/${s.currentCourseId}/topic/${s.lastLesson?.topic_id}/lesson/${s.lastLesson?.id}`;
                return `/course/${s.currentCourseId}/topic/${s.lastLesson?.topic_id}/lesson/${s.lastLesson?.id}?source=${p}`;
            },
            target: this.routerModule.events.routerPush
        });

        sample({
            clock: lastLessonInTopicStartedButtonClicked,
            source: {
                currentCourseId: this.currentCourseModule.stores.$currentCourseId,
                lastLessonInTopic: this.lessonsModule.stores.$lastLessonInTopic
            },
            filter: (s) => s.currentCourseId !== null && s.lastLessonInTopic !== null,
            fn: (s, p) => {
                if (!p)
                    return `/course/${s.currentCourseId}/topic/${s.lastLessonInTopic?.topic_id}/lesson/${s.lastLessonInTopic?.id}`;
                return `/course/${s.currentCourseId}/topic/${s.lastLessonInTopic?.topic_id}/lesson/${s.lastLessonInTopic?.id}?source=${p}`;
            },
            target: this.routerModule.events.routerPush
        });

        sample({
            clock: startLessonFlowRequested,
            fn: ({ courseId, topicId, lessonId, source }) =>
                `/course/${courseId}/topic/${topicId}/lesson/${lessonId}${source ? `?source=${source}` : ""}`,
            target: this.routerModule.events.routerPush
        });

        reset({
            clock: routePathNameChanged,
            target: $lessonModal
        });
    }
}

export { default as rehypeAccentColor } from "./rehype-accent-color";
export { default as rehypeBrBreak } from "./rehype-br-break";
export { default as rehypeTixuQuote } from "./rehype-tixu-quote";
